import * as CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";
import EncryptionKey from "../auth/EncryptionKey";
import { baseApiUrlList } from "./baseUrl";
 

const generateGuid = (): string => {
  return uuidv4();
};

const generateCheckSumValue = (dataToHash: string): string => {
  const modifiedString = dataToHash.substring(3, dataToHash.length - 3).trim();
  const oddIndexCharacters = getCharactersAtOddIndices(modifiedString);
  const checkSumKey = CryptoJS.HmacSHA256(oddIndexCharacters, process.env.REACT_APP_SECURE_KEY||"").toString(CryptoJS.enc.Hex);
  return checkSumKey;
};

const getCharactersAtOddIndices = (str: string): string => {
  const characters = str.split("");
  const oddIndexCharacters = characters.filter((_, index) => (index + 1) % 2 !== 0);
  return oddIndexCharacters.join("");
};

 

interface HeaderValues {
  getHeaders: (fileType: string, fileName: string) => {
    "Content-Type": string;
    "X-API-Key": string;
    "X-Random-Key": string;
    "X-CheckSumValue-Key": string;
    "Master-Access-Id": string;
    "Site-Id": any;
    "File-Type": string;
    "File-Name": string;
  };
}

const headerValues: HeaderValues = {
  getHeaders: (fileType: string, fileName: string) => {
    const generatedGuid = generateGuid();
    return {
      "Content-Type": "application/json",
      "X-API-Key": process.env.REACT_APP_KEY ||"",
      "X-Random-Key": generatedGuid,
      "X-CheckSumValue-Key": generateCheckSumValue(generatedGuid),
      "Site-Id": sessionStorage.getItem("siteId"),
      "Master-Access-Id": "",
      "File-Type": fileType,
      "File-Name": fileName,
      "Access-Control-Allow-Origin": "*", // This only works if the server allows it
      "Access-Control-Allow-Methods": "GET, POST, OPTIONS, PUT, DELETE",
      "Access-Control-Allow-Headers": "Content-Type, Authorization"
    };
  },
};

const updateCommonDetails = () => {
  return {
    lat: "",
    long: "",
    sourceId: "1",
    sourceDetails: "Web",
    languageToken: "en",
    tenantId: sessionStorage.getItem("tenantId"),
    siteId: sessionStorage.getItem("siteId"),
    siteUserId: sessionStorage.getItem("userId"),
    "Master-Access-Id": "",
    roleId:sessionStorage.getItem("roleId"),
  };
};

const requestDetails = (serviceDetails: any) => ({
  commonDetails: updateCommonDetails(),
  serviceDetails,
});

const makeRequest = async (url: string, method: string, data: any, fileType = "", id = "0", fileName = "") => {
  try { 
    const headers = headerValues.getHeaders(fileType, fileName);
    headers["Master-Access-Id"] = id;
    let body;
    if (data.serviceDetails instanceof ArrayBuffer) { 
      body =  data.serviceDetails ;
    } else {
      body = JSON.stringify(data);
    } 
    const response = await fetch(url, {
      method,
      headers,
      body
    });

    if (response.status === 200) {
      const responseData = await response.json();
      if (responseData.responseCode === "200") {
        return { success: true, data: responseData.responseResult };
      } else {
        const message = responseData.responseMessage.toString().trim().replace(/^"|"$/g, '') ;
        return { success: false, message: message };
      }
    } else {
      return { success: false, message: "Something went wrong. Please contact the administrator." };
    }
  } catch (error) {
    return { success: false, message: "Something went wrong. Please contact the administrator." };
  }
};

const createService = () => ({ 
  handleRequest: async (endpointKey: string, data: any, method = "POST", fileType = "", id = "0", isDirect = 0, fileName = "") => {
    const apiBaseURLs=await getApiBaseURLs();
    let baseURL:any;
    if(endpointKey==="api.web.domainValidate"){ 
      if(process.env.REACT_APP_ONPREM_BASE_API_URL){
        baseURL= `${process.env.REACT_APP_ONPREM_BASE_API_URL}/Web/DomainValidation`
      }
      else {
        baseURL= `${process.env.REACT_APP_BASE_API_URL}/Web/DomainValidation`;
      }
    }
    else if(endpointKey==="api.web.language"){
      baseURL = `${process.env.REACT_APP_BASE_API_URL}/GetLanguageMaster`;
    }
    else if(endpointKey==="api.web.samModelServices"){
      baseURL = `${process.env.REACT_APP_WRAPPER_URL}/SegmentModel`;
    } 
    else if(isDirect === 1){
      baseURL = endpointKey
    }
    else{
      baseURL = apiBaseURLs?.[endpointKey] ?? baseApiUrlList[endpointKey];
    }
    const requestData = requestDetails(data);
     
    return makeRequest(baseURL, method, requestData, fileType, id, fileName);
  }
});

const samService=  () => ({ 
  handleFormRequest : async (endpointKey: string, data: any, method = "POST", fileType = "", id = "0") => { 
  const apiBaseURLs = await getApiBaseURLs();
  let baseURL = apiBaseURLs[endpointKey];  
  return makeRequestFormData(baseURL, method, data, id);
}});
const makeRequestFormData = async (url: string, method: string, data: FormData, id = "0") => {
  try {
    const headers: any = {};
    headers["Master-Access-Id"] = id;

    // Since we're sending FormData, let the browser set the Content-Type
    const response = await fetch(url, {
      method,
      headers,
      body: data, // FormData is passed directly as the body
    });

    if (response.ok) {  // Checks for any 2xx status code
      const responseData = await response.json();
      if (responseData.responseCode === "200") {
        return { success: true, data: responseData.responseResult };
      } else {
        const message = responseData.responseMessage.toString().trim().replace(/^"|"$/g, '');
        return { success: false, message: message };
      }
    } else {
      const errorText = await response.text(); // Read the error response text
      return { success: false, message: `Request failed with status ${response.status}: ${errorText}` };
    }
  } catch (error) {
    console.error("Request error:", error); // Log the error for debugging
     
  }
};
const getApiBaseURLs = async () => { 
  try {
    const encryptedData = localStorage.getItem("serviceDetails");
    if (!encryptedData) {
      console.warn("No encrypted data found in localStorage.");
      return;
    }
    const primaryKey = EncryptionKey();
    const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, primaryKey||"");
    const decryptedData = decryptedBytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedData) {
      console.warn("Decryption failed or returned an empty result.");
      return;
    }
    return JSON.parse(decryptedData);
  } catch (error) {
    console.error("Error fetching API base URLs:", error);
  }
};

export { headerValues,  createService, getApiBaseURLs,updateCommonDetails, requestDetails, makeRequest,samService };
